import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { setDestroy } from "components/modalx/modalx.slice";
import Notice from "components/tw/notice";
import { btnDangerCls, btnSecondaryCls } from "components/tw/classes";
import { setOrderClosed } from "services/orders.service";
import { formatPeriodMonth } from "features/billing-period/billing-period";

type Props = {
  companyId: string;
  orderId: string;
  isRecurring?: boolean;
  /**
   * The month an arrears order will still bill after this close (YYYY-MM-01),
   * or null when nothing is pending.
   */
  arrearsPeriodFrom?: string | null;
  /**
   * The company has terminalas delivery on: a closed order stops being offered
   * when a loading order is created, and the dialog says so.
   */
  onTerminal?: boolean;
  /** Called after the order was closed, so the caller can refetch it. */
  onDone?: () => void;
};

/**
 * Confirmation for closing an order — a modalX component.
 *
 * Closing is not a display flag: it ends a recurring contract, freezes the
 * invoice settings and takes the order out of every create-invoice list.
 * Reopening a one-off order is open to whoever may close it, so the dialog
 * says that; reopening a recurring order restarts billing and stays with
 * administrators, so for that the person clicking this may not be able to undo
 * it themselves — say so plainly.
 *
 * An order billed in arrears still owes its final month after closing, and the
 * dialog says so: otherwise "closed" reads as "nothing more will be invoiced",
 * and the invoice that arrives on the next schedule day looks like a bug.
 */
export default function CloseOrderConfirm({
  companyId,
  orderId,
  isRecurring,
  arrearsPeriodFrom,
  onTerminal,
  onDone,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [closing, setClosing] = useState(false);

  const close = () => dispatch(setDestroy());

  const handleConfirm = async () => {
    setClosing(true);
    try {
      await setOrderClosed({ companyId, orderId, isClosed: true });
      toast.success(t("order_closed", { defaultValue: "Order closed" }));
      onDone?.();
      close();
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message || e?.message || t("error", { defaultValue: "Error" })
      );
    } finally {
      setClosing(false);
    }
  };

  return (
    <div className="space-y-4 text-sm text-zinc-700 dark:text-zinc-200">
      <p>{t("close_order_confirm_intro", { defaultValue: "Closing this order means:" })}</p>
      <ul className="list-disc space-y-1 pl-5">
        {isRecurring && <li>{t("close_order_confirm_recurring")}</li>}
        <li>
          {t("close_order_confirm_settings", {
            defaultValue: "Its invoice settings become read-only.",
          })}
        </li>
        <li>
          {t("close_order_confirm_list", {
            defaultValue: "It no longer appears when creating invoices for this customer.",
          })}
        </li>
        {onTerminal && (
          <li>
            {t("close_order_confirm_terminal", {
              defaultValue:
                "It no longer appears on the terminal when a loading order is created.",
            })}
          </li>
        )}
      </ul>
      {isRecurring && arrearsPeriodFrom ? (
        <Notice tone="warning">
          <p>
            {t("close_order_arrears_warning", {
              month: formatPeriodMonth(arrearsPeriodFrom),
            })}
          </p>
        </Notice>
      ) : null}
      <p className="text-zinc-500">
        {isRecurring
          ? t("close_order_confirm_reopen", {
              defaultValue:
                "Invoices already issued are not affected. Only an administrator can reopen a closed order.",
            })
          : t("close_order_confirm_reopen_same_people", {
              defaultValue:
                "Invoices already issued are not affected. Whoever can close an order can also reopen it.",
            })}
      </p>
      <div className="flex justify-end gap-2">
        <button type="button" className={btnSecondaryCls} onClick={close} disabled={closing}>
          {t("cancel", { defaultValue: "Cancel" })}
        </button>
        <button
          type="button"
          className={btnDangerCls}
          onClick={handleConfirm}
          disabled={closing}
        >
          {closing ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
          {t("close_order", { defaultValue: "Close order" })}
        </button>
      </div>
    </div>
  );
}
